import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { AdminDashboard } from "./components/AdminDashboard";
import { ChoiceGroup } from "./components/ChoiceGroup";
import { CtaPanel } from "./components/CtaPanel";
import { ModeToggle } from "./components/ModeToggle";
import { ProductPath } from "./components/ProductPath";
import { SectionNav } from "./components/SectionNav";
import { TechniqueCards } from "./components/TechniqueCards";
import { Timeline } from "./components/Timeline";
import { methodChoices, openingChoices, productInterestChoices, sections } from "./content/roadshow";
import { recordRoadshowEvent } from "./lib/analytics";

type Mode = "reading" | "presentation";
const MOBILE_READING_MODE_QUERY = "(max-width: 720px), (hover: none) and (pointer: coarse)";

function initialSectionId() {
  const hash = window.location.hash.replace("#", "");
  return sections.some((section) => section.id === hash) ? hash : "opening";
}

function initialMode(): Mode {
  if (typeof window.matchMedia !== "function") return "presentation";
  return window.matchMedia(MOBILE_READING_MODE_QUERY).matches ? "reading" : "presentation";
}

function scrollToSection(
  sectionId: string,
  options: { behavior?: ScrollBehavior; container?: HTMLElement | null } = {}
) {
  const section = document.getElementById(sectionId);
  if (!section) return;

  if (options.container) {
    if (options.behavior === "auto") {
      options.container.scrollTop = section.offsetTop;
    } else {
      options.container.scrollTo({
        top: section.offsetTop,
        behavior: options.behavior ?? "smooth"
      });
    }
  } else {
    section.scrollIntoView({ behavior: options.behavior ?? "smooth", block: "start" });
  }

  if (window.location.hash !== `#${sectionId}`) {
    history.replaceState(null, "", `#${sectionId}`);
  }
}

export function App() {
  const showAdmin = new URLSearchParams(window.location.search).get("admin") === "1";
  return showAdmin ? <AdminDashboard /> : <RoadshowPage />;
}

function RoadshowPage() {
  const [activeSection, setActiveSection] = useState(initialSectionId);
  const [mode, setMode] = useState<Mode>(initialMode);
  const shellRef = useRef<HTMLElement | null>(null);
  const transitionLock = useRef(false);
  const touchStartY = useRef<number | null>(null);
  const localTouchGesture = useRef(false);
  const viewedSections = useRef<Set<string>>(new Set());
  const didInitialPresentationScroll = useRef(false);
  const didInitialReadingScroll = useRef(false);
  const programmaticTarget = useRef<string | null>(null);
  const programmaticTimer = useRef<number | null>(null);
  const activeIndex = Math.max(
    sections.findIndex((section) => section.id === activeSection),
    0
  );

  const recordSectionView = useCallback((sectionId: string) => {
    if (viewedSections.current.has(sectionId)) return;
    viewedSections.current.add(sectionId);
    recordRoadshowEvent({
      sectionId,
      questionId: "section-visible",
      choice: sectionId,
      eventType: "slide_enter"
    });
  }, []);

  const scrollSectionInMode = useCallback(
    (sectionId: string, behavior: ScrollBehavior) => {
      if (programmaticTimer.current) {
        window.clearTimeout(programmaticTimer.current);
      }

      programmaticTarget.current = sectionId;
      scrollToSection(sectionId, {
        container: mode === "presentation" ? shellRef.current : null,
        behavior
      });

      if (behavior === "smooth") {
        programmaticTimer.current = window.setTimeout(() => {
          programmaticTarget.current = null;
          setActiveSection(sectionId);
          recordSectionView(sectionId);
        }, 820);
      } else {
        programmaticTarget.current = null;
        setActiveSection(sectionId);
        recordSectionView(sectionId);
      }
    },
    [mode, recordSectionView]
  );

  const navigateToIndex = useCallback(
    (index: number) => {
      const nextIndex = Math.min(Math.max(index, 0), sections.length - 1);
      const nextSection = sections[nextIndex];
      if (!nextSection) return;
      setActiveSection(nextSection.id);
      scrollSectionInMode(nextSection.id, "smooth");
    },
    [scrollSectionInMode]
  );

  const navigateByDirection = useCallback(
    (direction: number) => {
      const nextIndex = Math.min(Math.max(activeIndex + direction, 0), sections.length - 1);
      if (nextIndex === activeIndex) return;
      navigateToIndex(nextIndex);
    },
    [activeIndex, navigateToIndex]
  );

  useLayoutEffect(() => {
    document.documentElement.classList.toggle("roadshow-presentation", mode === "presentation");
    document.documentElement.classList.toggle("roadshow-reading", mode === "reading");
    return () => {
      document.documentElement.classList.remove("roadshow-presentation", "roadshow-reading");
    };
  }, [mode]);

  useLayoutEffect(() => {
    if (mode !== "presentation" || didInitialPresentationScroll.current) return;
    didInitialPresentationScroll.current = true;
    scrollSectionInMode(activeSection, "auto");
  }, [activeSection, mode, scrollSectionInMode]);

  useLayoutEffect(() => {
    if (mode !== "reading" || didInitialReadingScroll.current) return;
    didInitialReadingScroll.current = true;
    scrollSectionInMode(activeSection, "auto");
  }, [activeSection, mode, scrollSectionInMode]);

  useEffect(() => {
    return () => {
      if (programmaticTimer.current) {
        window.clearTimeout(programmaticTimer.current);
      }
    };
  }, []);

  useEffect(() => {
    const root = mode === "presentation" ? shellRef.current : null;
    const observer = new IntersectionObserver(
      (entries) => {
        if (programmaticTarget.current) return;
        const visible = entries.find((entry) => entry.isIntersecting);
        if (visible?.target.id) {
          setActiveSection(visible.target.id);
          recordSectionView(visible.target.id);
        }
      },
      { root, rootMargin: "-30% 0px -55% 0px", threshold: 0.1 }
    );

    sections.forEach((section) => {
      const node = document.getElementById(section.id);
      if (node) observer.observe(node);
    });

    return () => observer.disconnect();
  }, [mode, recordSectionView]);

  useEffect(() => {
    function handleHashChange() {
      const nextSectionId = initialSectionId();
      setActiveSection(nextSectionId);
      window.requestAnimationFrame(() => {
        scrollSectionInMode(nextSectionId, "smooth");
      });
    }

    window.addEventListener("hashchange", handleHashChange);
    return () => window.removeEventListener("hashchange", handleHashChange);
  }, [scrollSectionInMode]);

  useEffect(() => {
    if (mode !== "presentation") return;

    function handleKey(event: KeyboardEvent) {
      if (event.key !== "ArrowRight" && event.key !== "ArrowLeft") return;
      event.preventDefault();
      const direction = event.key === "ArrowRight" ? 1 : -1;
      navigateByDirection(direction);
    }

    window.addEventListener("keydown", handleKey);
    return () => window.removeEventListener("keydown", handleKey);
  }, [mode, navigateByDirection]);

  useEffect(() => {
    if (mode !== "presentation") return;
    const node = shellRef.current;
    if (!node) return;

    function navigateWithLock(direction: number) {
      if (transitionLock.current) return;
      transitionLock.current = true;
      navigateByDirection(direction);
      window.setTimeout(() => {
        transitionLock.current = false;
      }, 720);
    }

    function isLocalSwipeTarget(target: EventTarget | null) {
      return target instanceof Element && target.closest("[data-local-swipe]");
    }

    function handleWheel(event: WheelEvent) {
      if (isLocalSwipeTarget(event.target) && Math.abs(event.deltaX) > Math.abs(event.deltaY)) return;
      if (Math.abs(event.deltaY) < 36) return;
      event.preventDefault();
      navigateWithLock(event.deltaY > 0 ? 1 : -1);
    }

    function handleTouchStart(event: TouchEvent) {
      if (isLocalSwipeTarget(event.target)) {
        localTouchGesture.current = true;
        touchStartY.current = null;
        return;
      }
      localTouchGesture.current = false;
      touchStartY.current = event.touches[0]?.clientY ?? null;
    }

    function handleTouchMove(event: TouchEvent) {
      if (localTouchGesture.current) return;
      if (touchStartY.current !== null) event.preventDefault();
    }

    function handleTouchEnd(event: TouchEvent) {
      if (localTouchGesture.current) {
        localTouchGesture.current = false;
        return;
      }
      if (touchStartY.current === null) return;
      const endY = event.changedTouches[0]?.clientY ?? touchStartY.current;
      const distance = touchStartY.current - endY;
      touchStartY.current = null;
      if (Math.abs(distance) < 52) return;
      navigateWithLock(distance > 0 ? 1 : -1);
    }

    node.addEventListener("wheel", handleWheel, { passive: false });
    node.addEventListener("touchstart", handleTouchStart, { passive: true });
    node.addEventListener("touchmove", handleTouchMove, { passive: false });
    node.addEventListener("touchend", handleTouchEnd);

    return () => {
      node.removeEventListener("wheel", handleWheel);
      node.removeEventListener("touchstart", handleTouchStart);
      node.removeEventListener("touchmove", handleTouchMove);
      node.removeEventListener("touchend", handleTouchEnd);
    };
  }, [mode, navigateByDirection]);

  return (
    <main ref={shellRef} className={`app-shell mode-${mode}`}>
      <SectionNav activeSection={activeSection} />
      <ModeToggle mode={mode} onModeChange={setMode} />
      <div className="progress-pill">
        {activeIndex + 1} / {sections.length}
      </div>

      <section id="opening" className="roadshow-section hero-section">
        <div className="hero-inner">
          <div className="hero-content">
            <p className="eyebrow">Interactive Roadshow</p>
            <h1>
              <span>刷题宝：</span>
              <span>从 vibe coding</span>
              <span>到真实产品</span>
            </h1>
            <p className="hero-copy">
              这不是一页产品广告，而是一场把 AI 共创、真实踩坑、产品闭环和上线分享拆开讲清楚的互动路演。
            </p>
            <ChoiceGroup {...openingChoices} />
          </div>
          <figure className="hero-art" aria-label="AI sticker visual">
            <img src="/assets/vibe-stickers.png" alt="" />
          </figure>
        </div>
      </section>

      <section id="why" className="roadshow-section split-section">
        <div>
          <p className="eyebrow">Why It Matters</p>
          <h2>一个人备考真正缺的不是资料，而是资料变成行动的那条路。</h2>
        </div>
        <div className="text-stack">
          <p>资料散在 PDF、截图和笔记里，刷题和错题复盘分家，AI 也不知道你的题库、错题和距考时间。</p>
          <p>刷题宝要解决的是闭环：建档、喂资料、刷题、错题、AI 陪学和复盘节奏串在一起。</p>
        </div>
      </section>

      <section id="method" className="roadshow-section">
        <p className="eyebrow">Vibe Coding Method</p>
        <h2>vibe coding 不是让 AI 瞎写，而是人定义闭环，AI 加速执行。</h2>
        <div className="method-grid">
          <article><span>01</span><h3>讲清问题</h3><p>用户、场景、痛点、替代方案。</p></article>
          <article><span>02</span><h3>讲薄方案</h3><p>MVP、非目标、关键路径、验收标准。</p></article>
          <article><span>03</span><h3>交给 AI 执行</h3><p>给上下文、拆任务、守边界。</p></article>
          <article><span>04</span><h3>验收反馈</h3><p>测试、真实使用、复盘、继续迭代。</p></article>
        </div>
        <ChoiceGroup {...methodChoices} />
      </section>

      <section id="timeline" className="roadshow-section">
        <p className="eyebrow">Creation Timeline</p>
        <h2>从一个人的备考痛点，到一个可以公开分享的产品。</h2>
        <Timeline />
      </section>

      <section id="pitfalls" className="roadshow-section">
        <p className="eyebrow">Real Lessons</p>
        <h2>四个真实坑，长成四个能复用的 vibe coding 技巧。</h2>
        <TechniqueCards />
      </section>

      <section id="product" className="roadshow-section">
        <p className="eyebrow">Product Demo</p>
        <h2>现在打开刷题宝，看到的是这条产品路径。</h2>
        <ProductPath />
        <ChoiceGroup {...productInterestChoices} />
      </section>

      <section id="cta" className="roadshow-section">
        <p className="eyebrow">Next Step</p>
        <h2>试用刷题宝，也继续关注在瓶的 AI 产品创作。</h2>
        <CtaPanel />
      </section>
    </main>
  );
}
